import { memoize } from 'lodash';
import { logError } from '@/providers/ErrorTracking';
import axios from 'axios';
import { ADDRESS_COMPLETION_API_KEY } from '@/configuration/client';

interface AddressComponent {
  types: string[];
  longText: string;
  shortText?: string;
}

interface ZipCodeRange {
  start: number;
  end: number;
}

interface CountyMapping {
  ranges: ZipCodeRange[];
  county: string;
}

function formatCountyName(name: string): string {
  const lower = name.toLowerCase();
  if (!lower.includes('county')
      && !lower.includes('parish')
      && !lower.includes('borough')
      && !lower.includes('census area')
      && !lower.includes('municipality')) {
    return `${name} County`;
  }
  return name;
}

function findCountyFromDisplayName(displayName?: { text: string; languageCode: string } | string): string | undefined {
  const displayText = typeof displayName === 'string' ? displayName : displayName?.text;
  if (!displayText) return undefined;

  const parts = displayText.split(',');
  const countyPart = parts.find(part => {
    const lower = part.trim().toLowerCase();
    return lower.includes('county')
           || lower.includes('parish')
           || lower.includes('borough')
           || lower.includes('census area');
  });

  return countyPart ? formatCountyName(countyPart.trim()) : undefined;
}

function findCountyFromAddress(address?: string): string | undefined {
  if (!address) return undefined;

  const patterns = [
    /([A-Za-z\s]+) County/i,
    /([A-Za-z\s]+) Parish/i,
    /([A-Za-z\s]+) Borough/i,
    /([A-Za-z\s]+) Census Area/i,
  ];

  const matchingPattern = patterns.find(pattern => {
    const match = address.match(pattern);
    return match?.[0];
  });

  const match = matchingPattern ? address.match(matchingPattern) : null;
  return match?.[0] ? formatCountyName(match[0]) : undefined;
}

function findCountyFromComponents(components: AddressComponent[]): string | undefined {
  // check administrative_area_level_2
  const adminArea = components.find(
    component => component.types.includes('administrative_area_level_2'),
  );
  if (adminArea) {
    return formatCountyName(adminArea.longText);
  }

  // last try: look for specific terms
  const countyComponent = components.find(component => {
    const text = component.longText.toLowerCase();
    return text.includes('county')
           || text.includes('parish') // Louisiana
           || text.includes('borough') // Alaska
           || text.includes('census area'); // Alaska
  });

  return countyComponent ? formatCountyName(countyComponent.longText) : undefined;
}

const INDEPENDENT_CITIES_AND_COUNTIES: { [state: string]: CountyMapping[] } = {

  // District of Columbia
  'DC': [
    {
      county: 'District of Columbia',
      ranges: [{ end: 20099, start: 20001 }, { end: 20599, start: 20201 }],
    },
  ],

  // Maryland Independent City
  'MD': [
    {
      county: 'Baltimore City',
      ranges: [{ end: 21287, start: 21201 }],
    },
  ],

  // Missouri Independent City
  'MO': [
    {
      county: 'St. Louis City',
      ranges: [{ end: 63199, start: 63101 }],
    },
  ],

  // Nevada Independent City
  'NV': [
    {
      county: 'Carson City',
      ranges: [{ end: 89706, start: 89701 }],
    },
  ],

  // New York City's five counties and Nassau
  'NY': [
    // Manhattan
    {
      county: 'New York County',
      ranges: [{ end: 10282, start: 10001 }],
    },
    // Staten Island
    {
      county: 'Richmond County',
      ranges: [{ end: 10314, start: 10301 }],
    },
    // Bronx
    {
      county: 'Bronx County',
      ranges: [{ end: 10475, start: 10451 }],
    },
    // Brooklyn (Kings County)
    {
      county: 'Kings County',
      ranges: [
        { end: 11256, start: 11201 }, // Main Brooklyn
        { end: 11239, start: 11239 }, // Starrett City
      ],
    },
    // Queens specific ranges (must come before Nassau's wider range)
    {
      county: 'Queens County',
      ranges: [
        { end: 11120, start: 11101 }, // Long Island City
        { end: 11390, start: 11351 }, // Central Queens
        { end: 11436, start: 11411 }, // South Queens
        { end: 11697, start: 11691 }, // Rockaways
      ],
    },
    // Nassau County (catches remaining Long Island zipcodes)
    {
      county: 'Nassau County',
      ranges: [
        { end: 11096, start: 11001 }, // Western Nassau
        { end: 11598, start: 11501 }, // Central/Eastern Nassau
        { end: 11799, start: 11700 }, // South Shore
      ],
    },
  ],

  // Virginia Independent Cities (partial list - should it needs to be completed?)
  'VA': [
    {
      county: 'Richmond City',
      ranges: [{ end: 23298, start: 23218 }],
    },
    {
      county: 'Virginia Beach City',
      ranges: [{ end: 23467, start: 23451 }],
    },
  ],
};

function getIndependentCityOrCounty(zipcode: string): string | undefined {
  const zip = parseInt(zipcode, 10);
  if (Number.isNaN(zip)) return undefined;

  return Object.entries(INDEPENDENT_CITIES_AND_COUNTIES)
    .reduce<string | undefined>((result, [, stateMappings]) => {
      if (result) return result;

      return stateMappings.reduce<string | undefined>((countyResult, mapping) => {
        if (countyResult) return countyResult;

        const matchesRange = mapping.ranges.some(
          range => zip >= range.start && zip <= range.end,
        );

        return matchesRange ? mapping.county : undefined;
      }, undefined);
    }, undefined);
}

const getCountyFromAddressMemoized = memoize(async (
  { city, state, address, zipcode }:
  { city: string | null, state: string | null, address: string | null, zipcode: string },
): Promise<string> => {
  try {
    if (!zipcode) {
      return '';
    }

    // check independent cities and special counties first
    const directMapping = getIndependentCityOrCounty(zipcode);
    if (directMapping) {
      return directMapping;
    }

    const textQuery = [address, city, state, zipcode].filter(Boolean).join(', ');

    // API call for regular counties
    const googleResponse = await axios.post(
      'https://places.googleapis.com/v1/places:searchText',
      {
        languageCode: 'en',
        locationRestriction: {
          rectangle: {
            // Includes Hawaii
            high: { latitude: 72, longitude: -66 },
            low: { latitude: 19, longitude: -180 }, // Includes Alaska
          },
        },
        maxResultCount: 1,
        textQuery,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': ADDRESS_COMPLETION_API_KEY,
          'X-Goog-FieldMask': 'places.addressComponents,places.displayName,places.formattedAddress',
        },
      },
    );

    const place = googleResponse.data.places?.[0];
    if (!place?.addressComponents) {
      return '';
    }

    const county = findCountyFromComponents(place.addressComponents)
      || findCountyFromDisplayName(place.displayName)
      || findCountyFromAddress(place.formattedAddress);

    return county || '';

  } catch (error) {
    logError(error, {
      address,
      city,
      context: 'Google Places API',
      state,
      zipcode,
    });
    return '';
  }
});

export default getCountyFromAddressMemoized;
